import { observable, computed, action, asFlat } from 'mobx'
import { getQuestion, getPractice } from 'common/api'

export default class Practice {
  // ---------------------------------- Observable Define ----------------------------------

  @observable id = 0                            // 题目 id
  @observable name = ''                         // 题目 name
  @observable category = ''                     // 题目类型: radio | multi | blank | speaking | writing
  @observable material = {                      // 材料
    text: asFlat([]),
    picture: '',
    audio: ''
  }
  @observable answerTime = 0                    // 口语答题时间
  @observable textLimit = 0                     // 写作字数限制
  @observable tipId = 0                         // 逐题精讲开始tip
  @observable ctqa = asFlat({})                 // 逐题精讲
  @observable additionMaterial = asFlat({})     // 补充材料
  @observable detail = asFlat([])               // 题目详情
  @observable userAnswer = asFlat([])           // 用户答案
  @observable costTime = 0                      // 做题时间
  @observable questionIds = []                  // 练习的所有习题 id
  @observable practiceName = ''                 // 练习 name
  @observable show = {                          // 控制显示的变量
    timer: true,                                // 计时
    askTeacher: false,                          // 问问老师
    answer: false,                              // 偷看答案
    loading: false,                             // loading 数据
    login: false,                               // 登陆框
    writingOverflow: false,                     // 写作字数超限弹框
    writingErrorType: '' ,                    // 错误类型
    correction: false                       //显示我要纠错
  }
  @observable introduction = {text: []}                 // 指导语

  // ---------------------------------- Computed Define ----------------------------------

  @computed get isSingleLayout () {
    if (this.category === 'writing') { return false }
    if (this.category === 'speaking') { return true }

    const hasMaterial = this.material.text.length || this.material.picture || this.material.audio
    const onlyHasAudio = !this.material.text.length && !this.material.picture && this.material.audio

    return !hasMaterial || onlyHasAudio
  }

  @computed get idx () {
    return this.questionIds.indexOf(this.id)
  }

  @computed get hasNextQuestion () {
    return this.idx >= 0 && this.idx < this.questionIds.length - 1
  }

  @computed get hasPrevQuestion () {
    return this.idx > 0
  }

  // ---------------------------------- Action Define ----------------------------------

  @action toggleTimer (show) {
    sessionStorage.setItem('SHOW_TIMER', show)
    this.show.timer = show
  }

  @action toggleAskTeacher (show) {
    this.show.askTeacher = show
  }

  @action toggleAnswer (show) {
    this.show.answer = show
  }

  @action toggleLoading (show) {
    this.show.loading = show
  }

  @action toggleLogin (show) {
    this.show.login = show
  }

  @action toggleWritingOverflow (show, type) {
    this.show.writingOverflow = show
    this.show.writingErrorType = type
  }

  @action showCorrection (show) {
    this.show.correction = show
  }

  @action fetchQuestion (qid) {
    return getQuestion(qid)
      .then(action('fetchQuestion end', data => {
        this.id = data.id
        this.name = data.name
        this.category = data.category
        this.material = data.material
        this.additionMaterial = data.additionMaterial
        this.detail = data.detail
        this.ctqa = data.ctqa
        this.introduction = data.introduction
        this.answerTime = data.answerTime
        this.textLimit = data.textLimit
        this.tipId = data.tipId
        return data
      }))
  }

  @action fetchPractice (pid) {
    return getPractice(pid)
      .then(action('fetchPractice end', data => {
        this.practiceName = data.name
        this.questionIds = data.questionIds
      }))
  }

  @action fetchData (pid, qid) {
    pid = parseInt(pid, 10)
    qid = parseInt(qid, 10)

    if (pid === 0) {
      // 单个习题
      return this.fetchQuestion(qid)
        .then(action('set questionIds for single question', data => {
          this.questionIds = [data.id]
          this.initializeUserAnswer()
        }))
    } else {
      // 练习
      return Promise.all([this.fetchPractice(pid), this.fetchQuestion(qid)])
        .then(action('fetchData end', () => {
          this.initializeUserAnswer()
        }))
    }
  }

  @action initializeUserAnswer () {
    if (!this.userAnswer.length) {
      this.saveAnswer(this.detail.map((item, idx) => {
        switch (item.category) {
          case 'radio':
          case 'multi':
            return {
              index: idx,
              answer: '',
              isCorrect: false
            }
          case 'blank':
            return item.choices.map(blank => ({
              questionIndex: blank.question_index,
              answer: '',
              isCorrect: false
            }))
          case 'speaking':
            return {
              index: idx,
              audioPath: '',
              duration: 0
            }
          case 'writing':
            return {
              index: idx,
              answer: {
                text: '',
                wordCount: 0
              },
              isCorrect: false,
              isOverflow: false
            }
          default:
            return {}
        }
      }))
    }
  }

  @action loadSessionData (qid) {
    const ALL_ANSWERS = JSON.parse(sessionStorage.getItem('ALL_ANSWERS'))
    const SHOW_TIMER = JSON.parse(sessionStorage.getItem('SHOW_TIMER'))
    const COST_TIME = JSON.parse(sessionStorage.getItem('COST_TIME'))

    this.userAnswer = ALL_ANSWERS[qid]
    this.show.timer = SHOW_TIMER
    this.costTime = COST_TIME
  }

  @action resetState () {
    this.show.askTeacher = false
    this.show.answer = false
  }

  @action saveAnswer (answer) {
    const ALL_ANSWERS = JSON.parse(sessionStorage.getItem('ALL_ANSWERS')) || {}
    ALL_ANSWERS[this.id] = answer
    sessionStorage.setItem('ALL_ANSWERS', JSON.stringify(ALL_ANSWERS))

    this.userAnswer = answer
  }

  @action timePass () {
    this.costTime++
    sessionStorage.setItem('COST_TIME', this.costTime)
  }
}



/** WEBPACK FOOTER **
 ** ./src/models/Practice.js
 **/